<template>
  <div
    ref="videoItemWrapper"
    class="mc-video-history-new__video-item-wrapper"
    :class="{ fullscreen: isFullscreen }"
  >
    <b-spinner v-if="!iframeLoaded" />
    <iframe
      v-show="iframeLoaded"
      ref="iframeRef"
      :src="calculatedSrcIframe"
      allow="fullscreen"
      @load="onLoadIframe"
    />
    <div
      v-show="state === 'PLAYING' || state === 'PAUSED' || state === 'FREEZE'"
      class="mc-video-history-new__action-bar-wrapper"
    >
      <div class="left-action">
        <span class="toggle-muted-wrapper action-icon-wrapper" @click="toggleMuted">
          <i v-if="!isMuted" class="icon-outline_volume-off" />
          <i v-else class="icon-outline_volume" />
        </span>
        <span class="toggle-paused-wrapper action-icon-wrapper" @click="togglePaused">
          <i v-if="!isPaused" class="icon-fill_pause" />
          <i v-else class="icon-fill_play" />
        </span>
      </div>
      <div class="right-action">
        <span class="toggle-fill-fit-wrapper action-icon-wrapper" @click="toggleFillFit">
          <i v-if="isFill" class="mc-fa icon-fill_fit-screen"></i>
          <i v-else class="mc-fa icon-fill_fill-screen"></i>
        </span>
        <span class="toggle-fullscreen-wrapper action-icon-wrapper" @click="toggleFullscreen">
          <i v-if="!isFullscreen" class="icon-fill_fullscreen" />
          <i v-else class="icon-fill_exit-fullscreen" />
        </span>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import { Helper } from '@/mixin/helper'

moment.locale('id')

export default {
  name: 'VideoHistoryPlayerItem',
  mixins: [Helper],
  props: {
    channel: {
      type: Number,
      required: true,
    },
    state: {
      type: String,
      required: true,
    },
    imei: {
      type: String,
      required: true,
    },
  },
  emits: ['toggleMuted', 'togglePaused', 'toggleFillFit', 'toggleFullscreen'],
  data() {
    return {
      iframeLoaded: false,
      calculatedStartTime: null,
      calculatedEndTime: null,
      minutesPlaybackPerPart: 0.5,
      maxVideoDurationSeconds: 5 * 60,
      maxVideoSeconds: null,

      isPaused: false,
      isFill: true,
      isFullscreen: false,
      isMuted: false,
    }
  },
  computed: {
    ...mapState('videoHistory', [
      'activeTab',
      'filelistPlaySelected',
      'mapFilelistUnionPerChannel',
      'hasIntentToTimeChange',
      'dateTimeIntentToChangeInFilelist',
    ]),
    ...mapGetters('videoHistory', ['deviceTimezone', 'markerTimelineDate']),
    ...mapGetters('identity', ['getFeatureFlagValue']),
    calculatedSrcIframe() {
      const qParams = new URLSearchParams({
        imei: this.imei,
        channel: this.channel,
        startTime: this.calculatedStartTime,
        endTime: this.calculatedEndTime,
        isExtendedPlayback: 1,
        maxVideoSeconds: this.maxVideoSeconds,
        hideButtonInteraction: 1,
      })
      return `${process.env.VUE_APP_TRACKVISION_PUBLIC_API_URL}/video-history?${qParams.toString()}`
    },
  },

  watch: {
    state(videoState) {
      if (videoState === 'PAUSED') {
        this.isPaused = true
      } else {
        this.isPaused = false
      }
    },
  },

  async mounted() {
    const mapVideoHistoryTimeout = this.getFeatureFlagValue(
      'trackvision_map_company_video_history_timeout'
    )

    const videoHistoryTimeoutMs = mapVideoHistoryTimeout[this.getCompanyId()] ?? 5 * 60 * 1000
    this.maxVideoDurationSeconds = videoHistoryTimeoutMs / 1000
    await this.$nextTick()

    if (this.state === 'LOAD') {
      // LOAD iframe

      if (this.activeTab === 'timeline') {
        this.defineQueryParamsFromTimelineBar()
      } else if (this.activeTab === 'filelist') {
        this.defineQueryParamsFromFilelist()
      }

      this.$store.dispatch('videoHistory/registerStreamIframe', {
        imei: this.imei,
        channel: this.channel,
        iframe: this.$refs.iframeRef,
      })
      this.$store.commit('videoHistory/setHasIntentToTimeChange', false)
    }
    document.addEventListener('fullscreenchange', this.handleFullscreenChange)
  },

  beforeDestroy() {
    this.$store.dispatch('videoHistory/unregisterStreamIframe', {
      imei: this.imei,
      channel: this.channel,
    })
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
  },

  methods: {
    handleFullscreenChange() {
      this.isFullscreen = document.fullscreenElement === this.$refs.videoItemWrapper
    },
    calculateTimelinePlaybackRange({
      startTime,
      intervals,
      playbackPartMinutes,
      maximumVideoSeconds,
    }) {
      const start = moment(startTime, moment.ISO_8601, true)

      if (!start.isValid() || !Array.isArray(intervals)) return null

      const containingInterval = intervals.find(
        ({ startTime: intervalStart, endTime: intervalEnd }) => {
          const intervalStartTime = moment(intervalStart, moment.ISO_8601, true)
          const intervalEndTime = moment(intervalEnd, moment.ISO_8601, true)

          return (
            intervalStartTime.isValid() &&
            intervalEndTime.isValid() &&
            !start.isBefore(intervalStartTime) &&
            start.isBefore(intervalEndTime)
          )
        }
      )

      if (!containingInterval) return null

      const intervalEnd = moment(containingInterval.endTime, moment.ISO_8601, true)
      const remainingSeconds = Math.floor(intervalEnd.diff(start, 'milliseconds') / 1000)

      if (remainingSeconds <= 0) return null

      const playbackPartEnd = start.clone().add(playbackPartMinutes, 'minutes')

      return {
        endTime: moment.min(playbackPartEnd, intervalEnd).format('YYYY-MM-DDTHH:mm:ss'),
        maxVideoSeconds: Math.min(maximumVideoSeconds, remainingSeconds),
      }
    },
    defineQueryParamsFromFilelist() {
      if (
        !this.filelistPlaySelected.channel ||
        !this.filelistPlaySelected.mediaFilename ||
        !this.filelistPlaySelected.startTime ||
        !this.filelistPlaySelected.endTime
      )
        return

      // TODO : Adjust if next streaming
      let startLocalDate = this.convertUtcToLocalDate(
          this.filelistPlaySelected.startTime,
          this.deviceTimezone
        ),
        endLocalDate = this.convertUtcToLocalDate(
          this.filelistPlaySelected.endTime,
          this.deviceTimezone
        )

      if (this.hasIntentToTimeChange) {
        startLocalDate = this.convertUtcToLocalDate(
          this.dateTimeIntentToChangeInFilelist,
          this.deviceTimezone
        )
      }

      this.calculatedStartTime = startLocalDate

      const playbackRange = this.calculateTimelinePlaybackRange({
        startTime: startLocalDate,
        intervals: [
          {
            startTime: startLocalDate,
            endTime: endLocalDate,
          },
        ],
        playbackPartMinutes: this.minutesPlaybackPerPart,
        maximumVideoSeconds: this.maxVideoDurationSeconds,
      })

      if (!playbackRange) {
        this.calculatedEndTime = null
        this.maxVideoSeconds = null
        return
      }

      this.calculatedEndTime = playbackRange.endTime
      this.maxVideoSeconds = playbackRange.maxVideoSeconds
      this.$store.commit('videoHistory/setDateTimeIntentToChangeInFilelist', null)
    },

    defineQueryParamsFromTimelineBar() {
      const filelistUnion = this.mapFilelistUnionPerChannel[this.channel]

      this.calculatedStartTime = this.convertUtcToLocalDate(
        this.markerTimelineDate,
        this.deviceTimezone
      )

      const playbackRange = this.calculateTimelinePlaybackRange({
        startTime: this.calculatedStartTime,
        intervals: filelistUnion,
        playbackPartMinutes: this.minutesPlaybackPerPart,
        maximumVideoSeconds: this.maxVideoDurationSeconds,
      })

      if (!playbackRange) {
        this.calculatedEndTime = null
        this.maxVideoSeconds = null
        return
      }

      this.calculatedEndTime = playbackRange.endTime
      this.maxVideoSeconds = playbackRange.maxVideoSeconds
    },
    onLoadIframe() {
      this.iframeLoaded = true
    },
    toggleMuted() {
      const newVal = !this.isMuted
      this.isMuted = newVal
      this.$emit('toggleMuted', newVal)
    },
    togglePaused() {
      const newVal = !this.isPaused
      this.isPaused = newVal
      this.$emit('togglePaused', newVal)
    },
    setIsPaused(newVal) {
      this.isPaused = newVal
    },
    toggleFillFit() {
      const newVal = !this.isFill
      this.isFill = newVal
      this.$emit('toggleFillFit', newVal)
    },
    async toggleFullscreen() {
      const wrapper = this.$refs.videoItemWrapper
      if (!wrapper) return

      try {
        if (document.fullscreenElement === wrapper) {
          await document.exitFullscreen()
        } else if (!document.fullscreenElement) {
          await wrapper.requestFullscreen()
        }
      } catch (error) {
        console.error('Failed to toggle fullscreen', error)
      }
    },
  },
}
</script>
